import _ from "lodash";
import React from "react";
import client from "lib/apolloClient/client";

import {
  COURSE_BASED_GRADING,
  DEPENDENT,
  MYP_CRITERIA_BASED,
  MYP_CRITERIA_TOTAL,
  MYP_FINAL_GRADE,
  REGULAR,
  SCORE_BASED,
  STANDARD_BASED,
} from "Constants/stringConstants";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  MANAGE_COURSE_GRADE_SCALES_PERMISSION,
  MANAGE_COURSE_GRADING_CATEGORIES_PERMISSION,
  MANAGE_COURSE_GRADING_CONFIG_PERMISSION,
  MODULE_SETTINGS_GRADING_SETUP,
  CAN_MANAGE_STANDARDS,
  MANAGE_GRADE_BOUNDARIES_PERMISSION,
  MANAGE_ASSESSMENT_CATEGORY_PERMISSION,
  MANAGE_SUBJECT_SETTINGS_GRADING_CONFIG_PERMISSION,
} from "Constants/permissionConstants";
import {
  getFormattedGradingConfigSettingValue,
  getUpdatedCategorySets,
  normalizeSettingValue,
  normalizeTermWeight,
  getGradingConfigTypesFilter,
  getIsJsonstringifyUid,
  getUpdateGradeScaleEntityDetailsByType,
  shouldShowLevelsInGradingSetupV2,
} from "GradingSetupV2/modules/Utils";
import {
  getSubjectGradingConfigDetailsQuery,
  getCourseGradingConfigDetailsQuery,
  getSubjectGradeScaleDetailsQuery,
  getCourseGradeScaleDetailsQuery,
  getSubjectStandardSetsQuery,
  getLearningCourseStandardSetsQuery,
  getLearningCourseAssociatedLevelsQuery,
  getSubjectAssociatedLevelsQuery,
} from "GradingConfigurations/modules/GradingConfigurationQueries";
import {
  FINAL_GRADE_CALCULATION_GRADE_SCALE_UID,
  FINAL_GRADE_CALCULATION_METHODOLOGY_UID,
  FINAL_GRADE_CALCULATION_STANDARD_SET_LIST_UID,
  FINAL_GRADE_CALCULATION_STANDARD_SET_UID,
} from "./Constants";
import { getIsCourseBasedGradingEnabled } from "LearningCourses/modules/Utils";
import { updateGradeScaleMutation } from "Login/routes/Administrator/commonComponents/GradingSetupV2/modules/GradingSetupV2Mutations";

const styles = {
  tooltip: {
    display: "flex",
    flexDirection: "column",
    width: "240px",
  },
};

export const getInitialScoreCalculationSettings = ({
  settings,
  categorySets,
  cumulativeTermWeights,
}) => {
  const gradingPeriodWiseSetup = [];
  let initialCategories;

  _.forEach(categorySets, set => {
    const termId = _.get(set, "academicTerm.id", null);
    const gradingCategoryValues = _.map(
      _.get(set, "gradingCategoryValues", []),
      category => ({
        ...category,
        valueDetails: _.map(_.get(category, "valueDetails"), detail => {
          return {
            id: _.get(detail, "id"),
            weight: _.get(detail, "weight"),
            subjectLevelId: _.get(detail, "subjectLevel.id", null),
          };
        }),
      })
    );

    if (termId) {
      gradingPeriodWiseSetup.push({
        termId,
        categories: gradingCategoryValues,
      });
    } else {
      initialCategories = gradingCategoryValues;
    }
  });

  const state = {
    CATEGORY: {
      categories: initialCategories || [],
      gradingPeriodWiseSetup,
    },
    CUMULATIVE: {
      cumulativeTermWeights: _.map(
        cumulativeTermWeights,
        ({ cumulativeTermId, associatedTermWeights }) => ({
          cumulativeTermId,
          associatedTermWeights: _.map(
            associatedTermWeights,
            normalizeTermWeight
          ),
        })
      ),
    },
  };

  _.forEach(settings, ({ uid, value, groupType }) => {
    const normalizedValue = normalizeSettingValue(
      value,
      getIsJsonstringifyUid(uid)
    );

    if (state[groupType]) {
      state[groupType][uid] = normalizedValue;
    } else {
      state[groupType] = {
        [uid]: normalizedValue,
      };
    }
  });

  return state;
};

export const getUpdatedScoreBasedGradingConfigData = ({
  groupType,
  updatedData,
  settingsByUid,
  scoreCalculationSettings,
  updatedScoreCalculationSettings,
}) => {
  let updatedSettings,
    updatedCategorySets,
    addedCategorySets,
    cumulativeTermWeights;

  const updatedUids = Object.keys(updatedData);

  _.forEach(updatedUids, uid => {
    // TODO(@abhinay-toddleapp): To handle this in a better way
    if (groupType === "CUMULATIVE" && uid === "cumulativeTermWeights") {
      cumulativeTermWeights = updatedData[uid];
    } else if (
      groupType === "CATEGORY" &&
      (uid === "categories" || uid === "gradingPeriodWiseSetup")
    ) {
      const updateGradingCategorySets = getUpdatedCategorySets({
        scoreConfigSettings: updatedScoreCalculationSettings,
        prevScoreConfigSettings: scoreCalculationSettings,
      });

      updatedCategorySets = updateGradingCategorySets;
    } else {
      const settingId = _.get(settingsByUid, [uid, "id"]);

      const updatedSetting = {
        id: settingId,
        uid,
        value: getFormattedGradingConfigSettingValue(
          updatedData[uid],
          getIsJsonstringifyUid(uid)
        ),
        groupType,
      };

      if (updatedSettings) {
        updatedSettings.push(updatedSetting);
      } else {
        updatedSettings = [updatedSetting];
      }
    }
  });

  return {
    updatedSettings,
    addedCategorySets,
    updatedCategorySets,
    cumulativeTermWeights,
  };
};

export const getStandardConfigFromState = ({
  configSettings,
  learningCourseId,
  standardSetId,
  gradeScaleId,
  gradingConfigurationIds,
  academicYearId,
}) => {
  if (_.isEmpty(configSettings)) return null;

  const updateGradingConfigurationSettings = _.chain(configSettings)
    .pick(["HORIZONTAL", "VERTICAL"])
    .reduce((result, sectionValue, sectionKey) => {
      const sectionSettings = _.map(sectionValue, (value, key) => ({
        groupType: sectionKey,
        uid: key,
        value: getFormattedGradingConfigSettingValue(
          value,
          getIsJsonstringifyUid(key)
        ),
      }));

      return [...result, ...sectionSettings];
    }, [])
    .value();

  return {
    input: {
      entityType: "STANDARD_SET_GRADE_SCALE_MAP",
      entityDetails: {
        learningCourseId: learningCourseId,
        standardSetId,
        gradeScaleId,
      },
      gradingConfigurationIds,
      gradingConfigurationType: STANDARD_BASED,
      updateGradingConfigurationSettings,
      academicYearId,
    },
  };
};

export const getIsAddGradeScaleOptionDisabled = ({
  type,
  id,
  allGradeScales,
  initialGradeScales,
  selectedGradeScales: localSelectedGradeScales,
  dependentGradeScales,
  gradingMethodologyTypes,
  baseToDependentScalesMap,
  isAdminSetup = false,
}) => {
  const selectedGradeScales = _.union(
    localSelectedGradeScales,
    _.map(initialGradeScales, "id")
  );

  const isMYPCriteriaBasedMethodology = _.includes(
    gradingMethodologyTypes,
    MYP_CRITERIA_BASED
  );

  let validBaseScales = selectedGradeScales;

  if (isMYPCriteriaBasedMethodology) {
    const mypTotalGradeScale = _.find(
      allGradeScales,
      ({ node }) => _.get(node, "type") === MYP_CRITERIA_TOTAL
    );

    // this is to allow adding grade scales that depend on 0-32 grade scale when MYP total score is enabled
    if (mypTotalGradeScale) {
      validBaseScales = [
        ...selectedGradeScales,
        _.get(mypTotalGradeScale, "node.id"),
      ];
    }
  }

  switch (type) {
    case DEPENDENT: {
      const dependentScaleIds = _.map(dependentGradeScales, "id");

      const selectedDependentScaleIds = _.intersection(
        validBaseScales,
        dependentScaleIds
      );

      const isDisabled = _.size(selectedDependentScaleIds) !== 1;
      let tooltip = null;

      if (_.size(selectedDependentScaleIds) === 0) {
        tooltip = (
          // TODO: @anirudh localization
          <div style={styles.tooltip}>
            <span>
              This grade scale depends on a base grade scale to convert from.
              Add one of the following base grade scales first:
            </span>
            {_.map(dependentGradeScales, ({ id, displayName }) => {
              return <div key={id}>&bull; {displayName}</div>;
            })}
          </div>
        );
      } else if (_.size(selectedDependentScaleIds) > 1) {
        tooltip = (
          <div style={styles.tooltip}>
            <span>
              This grade scale converts from multiple grade scales added to the
              course. Remove one of the following grade scales to add this grade
              scale to the course
            </span>
            {_.map(selectedDependentScaleIds, id => {
              const { displayName } =
                _.find(dependentGradeScales, { id }) || {};

              return <div key={id}>&bull; {displayName}</div>;
            })}
          </div>
        );
      }

      return {
        isDisabled,
        tooltip,
      };
    }
    case REGULAR:
    case MYP_FINAL_GRADE: {
      const dependentScales = baseToDependentScalesMap[id] || [];
      const selectedDependentScaleIds = _.intersection(
        validBaseScales,
        dependentScales
      );

      const isMultipleDependentScalesSelected =
        !_.includes(validBaseScales, id) &&
        _.size(selectedDependentScaleIds) === 1;

      let isDisabled = isMultipleDependentScalesSelected;

      if (type === MYP_FINAL_GRADE) {
        isDisabled =
          isMultipleDependentScalesSelected || !isMYPCriteriaBasedMethodology;
      }

      const tooltip = isMultipleDependentScalesSelected
        ? "This course already has a grade scale that the dependent scale is converting from. Remove it to add a new base scale."
        : null;

      return { isDisabled, tooltip };
    }
    default: {
      return {};
    }
  }
};

export const getGradingConfigDetailsQueryAndVarsByType = ({
  academicSetupType,
  subjectId,
  primaryGradeId,
  learningCourseId,
  curriculumProgramType,
  academicYearId,
}) => {
  const configTypesFilter = getGradingConfigTypesFilter(curriculumProgramType);

  switch (academicSetupType) {
    case COURSE_BASED_GRADING: {
      return {
        standardSetsQuery: getLearningCourseStandardSetsQuery,
        standardSetsVariables: {
          id: learningCourseId,
        },
        gradeScaleQuery: getCourseGradeScaleDetailsQuery,
        gradingConfigDetailsQuery: getCourseGradingConfigDetailsQuery,
        gradingConfigDetailsVariables: {
          id: learningCourseId,
          gradingConfigFilters: {
            type: configTypesFilter,
          },
        },
        gradeScaleVariables: {
          id: learningCourseId,
          filters: {
            isArchived: false,
            types: ["REGULAR", "DEPENDENT"],
            entityType: "LEARNING_COURSE",
            entityDetailFilters: [
              {
                learningCourseId,
              },
            ],
          },
          gradeScaleEntityDetailsFilters: {
            entityType: "LEARNING_COURSE",
            entityDetails: [
              {
                learningCourseId,
              },
            ],
          },
        },
      };
    }
    default: {
      return {
        standardSetsQuery: getSubjectStandardSetsQuery,
        standardSetsVariables: {
          id: subjectId,
        },
        gradeScaleQuery: getSubjectGradeScaleDetailsQuery,
        gradingConfigDetailsQuery: getSubjectGradingConfigDetailsQuery,
        gradingConfigDetailsVariables: {
          id: subjectId,
          gradingConfigFilters: {
            gradeIds: primaryGradeId,
            type: configTypesFilter,
            academicYearId,
          },
          gradingMethodologyFilters: {
            gradeIds: primaryGradeId,
            academicYearId,
          },
        },
        gradeScaleVariables: {
          id: subjectId,
          filters: {
            gradeIds: primaryGradeId,
          },
          gradeScaleEntityDetailsFilters: {
            entityType: "SUBJECT_GRADE_MAP",
            entityDetails: [
              {
                subjectId,
                gradeId: primaryGradeId,
              },
            ],
          },
        },
      };
    }
  }
};

export const parseGradingConfigDetailsByType = ({
  data,
  academicSetupType,
}) => {
  const node = _.get(data, "node", {});

  const scoreBasedConfig = _.find(_.get(node, "gradingConfigurations"), {
    type: SCORE_BASED,
  });

  let gradingMethodologyTypes;

  switch (academicSetupType) {
    case COURSE_BASED_GRADING:
      {
        gradingMethodologyTypes = _.get(
          node,
          "gradingMethodology.gradingMethodologyTypes"
        );
      }
      break;
    default: {
      gradingMethodologyTypes = _.get(
        node,
        "gradingMethodologies.0.gradingMethodologyTypes"
      );
    }
  }

  return {
    scoreBasedConfig,
    gradingMethodologyTypes,
  };
};

export const getGradingMethodologyMutationVarsByType = ({
  subjectId,
  primaryGradeId,
  academicYearId,
  learningCourseId,
  academicSetupType,
  gradingMethodologyTypes,
}) => {
  switch (academicSetupType) {
    case COURSE_BASED_GRADING: {
      return {
        input: {
          entityType: "LEARNING_COURSE",
          entityDetail: {
            learningCourseId,
          },
          gradingMethodologyTypes,
        },
      };
    }
    default: {
      return {
        input: {
          entityType: "SUBJECT_GRADE_MAP",
          entityDetail: {
            subjectId,
            gradeId: primaryGradeId,
          },
          gradingMethodologyTypes,
          academicYearId,
        },
      };
    }
  }
};

export const getImportGradeScalesVariablesByType = ({
  subjectId,
  primaryGradeId,
  gradingPeriodId,
  learningCourseId,
  addedGradeScales,
  academicSetupType,
}) => {
  switch (academicSetupType) {
    case COURSE_BASED_GRADING: {
      return {
        input: {
          gradeScaleIds: addedGradeScales,
          importToEntityDetails: {
            learningCourseId,
            academicTermIds: [gradingPeriodId],
          },
        },
      };
    }
    default: {
      return {
        input: {
          gradeScaleIds: addedGradeScales,
          importToEntityDetails: {
            subjectId,
            gradeId: primaryGradeId,
            academicTermIds: [gradingPeriodId],
          },
        },
      };
    }
  }
};

export const getDeleteGradeScalesVariablesByType = ({
  subjectId,
  primaryGradeId,
  learningCourseId,
  gradingPeriodId,
  academicSetupType,
  removedGradeScales,
}) => {
  switch (academicSetupType) {
    case COURSE_BASED_GRADING: {
      return {
        input: _.map(removedGradeScales, id => ({
          id,
          entityType: "LEARNING_COURSE",
          entityDetail: {
            learningCourseId,
            academicTermIds: [gradingPeriodId],
          },
        })),
      };
    }
    default: {
      return {
        input: _.map(removedGradeScales, id => ({
          id,
          entityType: "SUBJECT_GRADE_MAP",
          entityDetail: {
            subjectId,
            gradeId: primaryGradeId,
            academicTermIds: [gradingPeriodId],
          },
        })),
      };
    }
  }
};

export const getDialogMessageDataByType = ({
  displayName,
  subjectName,
  primaryGradeLabel,
  academicSetupType,
  learningCourseTitle,
  gradingPeriodLabel,
}) => {
  // TODO: @anirudh localization
  switch (academicSetupType) {
    case COURSE_BASED_GRADING: {
      return {
        message: `You are removing ‘${displayName}’ final grade scale from ‘${gradingPeriodLabel}’ for course ‘${learningCourseTitle}’. This will automatically remove it from the gradebook and unlocked progress reports. Would you still like to continue?`,
      };
    }
    default: {
      return {
        message: `You are removing ‘${displayName}’ final grade scale from ‘${gradingPeriodLabel}’ for subject ‘${subjectName}’. This will automatically remove it from the gradebook and unlocked progress reports. Would you still like to continue?`,
        subjectBasedAlertMsg: `Please note that this change will impact all ${primaryGradeLabel} courses of subject ‘${subjectName}’.`,
      };
    }
  }
};

const getPermValue = ({ permission, skipCheck }) => {
  return !skipCheck ? ACLStorage.getPermissionValue(permission) : true;
};

export const getGradingConfigRelatedPerms = ({
  isClassSettingContext = false,
}) => {
  const isRnPEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
  const skipCheck = !isRnPEnabled;

  const gradingConfigPerm = isClassSettingContext
    ? MANAGE_SUBJECT_SETTINGS_GRADING_CONFIG_PERMISSION
    : MANAGE_COURSE_GRADING_CONFIG_PERMISSION;
  const canManageGradingConfig = getPermValue({
    permission: gradingConfigPerm,
    skipCheck,
  });

  const manageCategoriesPerm = isClassSettingContext
    ? MANAGE_ASSESSMENT_CATEGORY_PERMISSION
    : MANAGE_COURSE_GRADING_CATEGORIES_PERMISSION;
  const canManageCategories = getPermValue({
    permission: manageCategoriesPerm,
    skipCheck,
  });

  const manageGradeScalesPerm = isClassSettingContext
    ? MANAGE_GRADE_BOUNDARIES_PERMISSION
    : MANAGE_COURSE_GRADE_SCALES_PERMISSION;
  const canManageGradeScales = getPermValue({
    permission: manageGradeScalesPerm,
    skipCheck,
  });

  const canConfigureStandards = getPermValue({
    permission: CAN_MANAGE_STANDARDS,
    skipCheck,
  });

  const canManageStandardConfig = skipCheck
    ? true
    : ACLStorage.getModuleSettingsValue(MODULE_SETTINGS_GRADING_SETUP);

  return {
    canManageCategories,
    canManageGradeScales,
    canManageGradingConfig,
    canManageStandardConfig,
    canConfigureStandards,
  };
};

// * Return selected term's final grade scale calculation settings
export const getFinalGradeCalculationSettings = ({
  gradeScale,
  termId,
  subjectId,
  gradeId,
  learningCourseId,
  isAdminSetup = false,
  useFirstEntity = false,
}) => {
  const isCourseBasedGrading = getIsCourseBasedGradingEnabled();

  const allEntityDetails = _.get(gradeScale, "gradeScaleEntityDetails");

  // * Find matching entity with academic term for course based || academic term, subject, grade for subject based
  let matchingEntity = {};

  if (useFirstEntity) {
    matchingEntity = _.first(allEntityDetails);
  } else if (isAdminSetup) {
    // Admin setup uses academicTerm.id, while course/subject setup uses entityDetail.academicTermId
    matchingEntity = _.find(
      allEntityDetails,
      entityDetail =>
        _.get(entityDetail, "academicTerm.id") === termId ||
        _.get(entityDetail, "entityDetail.academicTermId") === termId
    );
  } else if (!isCourseBasedGrading) {
    matchingEntity = _.find(allEntityDetails, ({ entityDetail }) => {
      const {
        subjectId: entitySubjectId,
        gradeId: entityGradeId,
        academicTermId: entityAcademicTermID,
      } = entityDetail || {};

      return (
        entityAcademicTermID === termId &&
        entitySubjectId === subjectId &&
        entityGradeId === gradeId
      );
    });
  } else {
    matchingEntity = _.find(allEntityDetails, ({ entityDetail }) => {
      const {
        learningCourseId: entityLearningCourseId,
        academicTermId: entityAcademicTermId,
      } = entityDetail || {};

      return (
        entityLearningCourseId === learningCourseId &&
        entityAcademicTermId === termId
      );
    });
  }

  const settings = _.get(matchingEntity, "gradingConfiguration.settings", []);

  const settingsObj = _.reduce(
    settings,
    (acc, { uid, value }) => {
      acc[uid] = value;
      return acc;
    },
    {}
  );

  return {
    [FINAL_GRADE_CALCULATION_METHODOLOGY_UID]: _.get(
      settingsObj,
      FINAL_GRADE_CALCULATION_METHODOLOGY_UID,
      null
    ),
    [FINAL_GRADE_CALCULATION_STANDARD_SET_UID]: _.get(
      settingsObj,
      FINAL_GRADE_CALCULATION_STANDARD_SET_UID,
      null
    ),
    [FINAL_GRADE_CALCULATION_STANDARD_SET_LIST_UID]: _.get(
      settingsObj,
      FINAL_GRADE_CALCULATION_STANDARD_SET_LIST_UID,
      null
    ),
    [FINAL_GRADE_CALCULATION_GRADE_SCALE_UID]: _.get(
      settingsObj,
      FINAL_GRADE_CALCULATION_GRADE_SCALE_UID,
      null
    ),
    gradeScaleEntityDetailId: _.get(matchingEntity, "id", null),
  };
};

export const parseStandardSetsByType = ({ data, academicSetupType }) => {
  switch (academicSetupType) {
    case COURSE_BASED_GRADING:
      return _.get(data, "node.standardsWrapper.standardsEdge");
    default: {
      const plannerElementNodeSet = _.get(
        data,
        "node.plannerElementNodeSet",
        {}
      );
      const { id, name } = _.get(data, "node", {});

      return [
        {
          id,
          standardSet: {
            id,
            title: name,
            ...plannerElementNodeSet,
          },
        },
      ];
    }
  }
};

export const getCourseOptionsForSidePane = ({ courses }) => {
  const root = {
    key: "root",
    children: _.map(courses, "id"),
  };

  const options = _.map(courses, ({ id, title }) => {
    return {
      key: id,
      title,
      tier: "1",
      parent: "root",
    };
  });

  return [root, ...options];
};

export const getCanSelectMultipleStandardsForGradeScale = () =>
  ACLStore.can("FeatureFlag:EnableMultipleStandardSetsSelectionForFinalGrade");

export const getSelectedStandardSetsForGradeScale = config => {
  let selectedStandardSets = [];

  const selectedStandardSetList = _.get(
    config,
    FINAL_GRADE_CALCULATION_STANDARD_SET_LIST_UID,
    null
  );

  if (selectedStandardSetList) {
    try {
      selectedStandardSets = getCanSelectMultipleStandardsForGradeScale()
        ? JSON.parse(selectedStandardSetList)
        : _.first(JSON.parse(selectedStandardSetList));
    } catch (e) {
      console.error(e);
    }
  } else {
    const legacyStandardSetValue = _.get(
      config,
      FINAL_GRADE_CALCULATION_STANDARD_SET_UID,
      null
    );
    if (legacyStandardSetValue) {
      selectedStandardSets = [legacyStandardSetValue];
    }
  }

  return _.castArray(selectedStandardSets);
};

export const updateGradeScaleIsPrimaryStatus = async ({
  gradeScaleId,
  isPrimary,
  subjectId,
  primaryGradeId,
  gradingPeriodId,
  learningCourseId,
  isCurriculumLevel,
  academicSetupType,
  curriculumProgramId,
}) => {
  const { entityType, entityDetail } = getUpdateGradeScaleEntityDetailsByType({
    subjectId,
    primaryGradeId,
    gradingPeriodId,
    learningCourseId,
    isCurriculumLevel,
    academicSetupType,
    curriculumProgramId,
  });

  await client.mutate({
    mutation: updateGradeScaleMutation,
    variables: {
      input: {
        id: gradeScaleId,
        entityType,
        entityDetail,
        isPrimary,
      },
    },
  });
};

export const getGradingConfigLockStatus = ({ data, field }) => {
  const syncData = _.get(data, "teacherCourseChangeSyncDataV3", []);
  const currentNode = _.find(syncData, item => _.startsWith(item.id, field));
  const isLocked = _.get(currentNode, "lockingInfo.isLocked", false);

  return isLocked;
};

export const getGradingConfigLevelsQueryConfig = ({
  isLearningCoursesEnabled,
  learningCourseId,
  subjectId,
  academicSetupType,
  curriculumProgramId,
}) => {
  if (isLearningCoursesEnabled) {
    return {
      query: getLearningCourseAssociatedLevelsQuery,
      variables: { id: learningCourseId },
      parseData: data =>
        academicSetupType === COURSE_BASED_GRADING
          ? _.get(data, "node.associatedLevels", [])
          : _.get(
              _.find(_.get(data, "node.subjectLevelMap"), item => {
                return _.get(item, "subject.id") === subjectId;
              }),
              "associatedLevels",
              []
            ),
      skipCondition:
        _.isEmpty(learningCourseId) ||
        !shouldShowLevelsInGradingSetupV2(curriculumProgramId),
    };
  }

  return {
    query: getSubjectAssociatedLevelsQuery,
    variables: { id: subjectId },
    parseData: data =>
      _.chain(_.get(data, "node.flatVariants", []))
        .uniqBy("level.id")
        .map("level")
        .value(),
    skipCondition:
      _.isEmpty(subjectId) ||
      !shouldShowLevelsInGradingSetupV2(curriculumProgramId),
  };
};
